// src/components/reporting/usage/UsageTenantList.js
// Renders the list of tenant summary rows at the systemwide level.
// Each row shows: tenant name, total calls, total cost, per-service call counts.
// Clicking a row drills into the tenant detail view.

import React from 'react';
import PropTypes from 'prop-types';
import { centsToDollars } from './UsageSummaryPanel';

const SERVICE_LABELS = {
  conversation:    'Conv.',
  analytic_report:   'Analytics',
  ad_hoc_report:     'Ad Hoc',
  assessment_report: 'Assessments',
  stt:               'STT',
  tts:             'TTS',
};

const UsageTenantList = ({ tenantSummaries, onSelectTenant }) => {
  if (!tenantSummaries || tenantSummaries.length === 0) {
    return (
      <div className="usage-tenant-list usage-empty">
        No tenants had activity during this period.
      </div>
    );
  }

  return (
    <div className="usage-tenant-list">
      {/* Table headers */}
      <div className="usage-tenant-header">
        <div className="utl-name-col">Tenant</div>
        {Object.entries(SERVICE_LABELS).map(([key, label]) => (
          <div key={key} className="utl-service-col">{label}</div>
        ))}
        <div className="utl-calls-col">Total Calls</div>
        <div className="utl-cost-col">Total Cost</div>
        <div className="utl-action-col"></div>
      </div>

      {/* Tenant rows */}
      {tenantSummaries.map((tenant) => {
        const { tenantId, tenantName, tenantSlug, services, totals } = tenant;
        return (
          <div
            key={tenantId}
            className="usage-tenant-row clickable-card"
            onClick={() => onSelectTenant(tenant)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onSelectTenant(tenant)}
            aria-label={`View usage details for ${tenantName}`}
          >
            {/* Name */}
            <div className="utl-name-col">
              <span className="utl-tenant-name">{tenantName}</span>
              <span className="utl-tenant-slug">{tenantSlug}</span>
            </div>

            {/* Per-service call counts */}
            {Object.keys(SERVICE_LABELS).map((key) => (
              <div key={key} className="utl-service-col">
                <span className="utl-service-value">
                  {services?.[key]?.callCount
                    ? Number(services[key].callCount).toLocaleString()
                    : '—'}
                </span>
              </div>
            ))}

            {/* Totals */}
            <div className="utl-calls-col">
              <span className="utl-total-calls">
                {Number(totals?.totalCallCount ?? 0).toLocaleString()}
              </span>
            </div>
            <div className="utl-cost-col">
              <span className="utl-total-cost">
                {centsToDollars(totals?.totalCostCents)}
              </span>
            </div>

            {/* Chevron */}
            <div className="utl-action-col">
              <svg className="utl-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        );
      })}
    </div>
  );
};

UsageTenantList.propTypes = {
  tenantSummaries: PropTypes.arrayOf(
    PropTypes.shape({
      tenantId:   PropTypes.number.isRequired,
      tenantName: PropTypes.string.isRequired,
      tenantSlug: PropTypes.string,
      services:   PropTypes.object,
      totals:     PropTypes.shape({
        totalCostCents:  PropTypes.number,
        totalCallCount:  PropTypes.number,
      }),
    })
  ),
  onSelectTenant: PropTypes.func.isRequired,
};

export default UsageTenantList;